import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { linkChild } from '../services/parentApi';

const PARENT_CODE_KEY = 'methodix_parent_code';

export default function Register() {
  const [step, setStep] = useState<'register' | 'confirm'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkingChild, setLinkingChild] = useState(false);
  const { register, confirmRegistration, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Stocker le code parent de l'URL dans sessionStorage
  useEffect(() => {
    const parentCode = searchParams.get('code');
    if (parentCode) {
      sessionStorage.setItem(PARENT_CODE_KEY, parentCode);
    }
  }, [searchParams]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name);
      setStep('confirm');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmRegistration(email, code);
      await login(email, password);

      // Auto-link enfant si un code parent était dans l'URL
      const savedCode = sessionStorage.getItem(PARENT_CODE_KEY);
      if (savedCode) {
        setLinkingChild(true);
        try {
          await linkChild(savedCode);
          sessionStorage.removeItem(PARENT_CODE_KEY);
          navigate('/'); // Redirige vers le dashboard avec l'enfant déjà lié
          return;
        } catch (linkErr: any) {
          console.warn('Auto-link failed:', linkErr.message);
          // Si le link échoue, on redirige vers la page de link avec le code pré-rempli
          sessionStorage.removeItem(PARENT_CODE_KEY);
          navigate(`/link?code=${encodeURIComponent(savedCode)}`);
          return;
        }
      }

      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Erreur de confirmation');
    } finally {
      setLoading(false);
      setLinkingChild(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <span className="text-5xl">📚</span>
          <h2 className="mt-4 text-3xl font-bold text-slate-900">
            {step === 'register' ? 'Créer un compte' : 'Vérification'}
          </h2>
          <p className="mt-2 text-slate-600">
            {step === 'register'
              ? 'Inscrivez-vous pour suivre votre enfant'
              : 'Entrez le code reçu par email'}
          </p>
        </div>

        {step === 'register' ? (
          <form onSubmit={handleRegister} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                Nom
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Jean Dupont"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="parent@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="••••••••"
              />
              <p className="mt-1 text-xs text-slate-500">Minimum 8 caractères</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-slate-700">
                Code de vérification
              </label>
              <input
                id="code"
                type="text"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest"
                placeholder="123456"
                maxLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {linkingChild ? 'Liaison de votre enfant...' : loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-600">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}

